import dayjs from "dayjs";
import { IGeneralInfo } from "../models/generalInfo";
import { IPayrollEntryDay, IPayrollEntryDayAdditionalRetention } from "../models/payroll";
import Util from "./util";

// Only what the payroll needs from a weekly report.
export interface ISlimReportEntry {
    employee_id: number;
    total_amount: number;
}

export interface ISlimReport {
    id: number;
    report_date: string;
    report_entries: ISlimReportEntry[];
}

export const getHoursSalary = (
    employeeId: number,
    entryDate: string | null,
    reports: ISlimReport[],
): number => {
    if (!entryDate) return 0;
    const myDate = dayjs(entryDate);
    const report = reports.find(r => dayjs(r.report_date).isSame(myDate, "day"));
    if (!report) return 0;
    const entry = report.report_entries.find(e => e.employee_id === Number(employeeId));
    return entry?.total_amount ?? 0;
}

const round2 = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const calcDayTotal = (
    employeeId: number,
    entryDate: string | null,
    otherDeductions: number,
    additional: number,
    additionalRetentions: IPayrollEntryDayAdditionalRetention[],
    generalInfo: IGeneralInfo | null,
    generalInfos: IGeneralInfo[],
    reports: ISlimReport[],
): { total: number; totalDeductions: number } => {
    if (!generalInfo) return { total: 0, totalDeductions: 0 };
    const infoEntry = generalInfo.general_info_entries.find(e => e.employee_id === Number(employeeId));
    if (!infoEntry) return { total: 0, totalDeductions: 0 };

    const salary = infoEntry.salary / 4.33;
    const afp = salary * 0.0287;
    const ars = salary * 0.0304;
    const hoursSalary = getHoursSalary(employeeId, entryDate, reports);
    const deductExcl = Util.getTotalDeductionsExcludingLoans(generalInfos, generalInfo.id, Number(employeeId));
    const deductLoans = Util.getTotalDeductionsFromLoans(generalInfos, generalInfo.id, Number(employeeId), additionalRetentions);
    const totalDeductions = Number(otherDeductions) + afp + ars + deductExcl + deductLoans;
    const total = salary - totalDeductions + Number(additional) + hoursSalary;
    return { total: round2(total), totalDeductions: round2(totalDeductions) };
}

// The entry-level figure is the sum of what every day deducts. Days with a
// custom total are excluded because their amount is typed by hand, and
// tombstoned days are already on their way out.
export const computeEntryTotalDeductions = (
    days: IPayrollEntryDay[],
    employeeId: number,
    otherDeductions: number,
    generalInfo: IGeneralInfo | null,
    generalInfos: IGeneralInfo[],
    reports: ISlimReport[],
): number => {
    const total = days
        .filter(d => !d._destroy && !d.is_custom)
        .reduce((sum, d) => sum + calcDayTotal(
            employeeId,
            d.entry_date as unknown as string | null,
            otherDeductions,
            0,
            (d.payroll_entry_day_additional_retentions ?? []).filter(r => !r._destroy),
            generalInfo,
            generalInfos,
            reports,
        ).totalDeductions, 0);

    return round2(total);
}
